import { useEffect, useMemo, useOptimistic, useRef, useTransition } from 'react';
import { Dropdown } from '@geberit/gdds';
import type { SelectOption } from '@geberit/gdds/dist/esm/form/Dropdown/types/Dropdown.types';
import debounce from 'lodash.debounce';

// styles
import styles from './tiles-filter.module.scss';

// types
import type { SelectableElements } from './tiles-filter';

// utils
import { useSearchParamsState } from 'utils/hooks/use-search-params-state';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isEmpty } from 'utils/is-empty';

type FilterDropdownProps = {
  id: string;
  label: string;
  allItems: SelectOption[];
  placeholder: string;
  filterName: SelectableElements;
  selectedText?: string;
  selectedTextPlural?: string;
  fsSectionId: string;
};

export function buildPrefixedFilterIdentifier(fsSectionId: string, filterName: string) {
  return `${fsSectionId}_${filterName}`;
}

export function FilterDropdown({
  id,
  label,
  allItems,
  placeholder,
  filterName,
  selectedText,
  selectedTextPlural,
  fsSectionId,
}: Readonly<FilterDropdownProps>) {
  const uniqueFilterName = buildPrefixedFilterIdentifier(fsSectionId, filterName);
  const [__, setStartTransition] = useTransition();
  const translate = useTranslationFunction();
  const [searchParamsState, _, setMultipleSelectedParams] = useSearchParamsState(
    [uniqueFilterName],
    [],
    true,
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [optimisticState, setOptimisticState] = useOptimistic(searchParamsState, (_, curr) => {
    return curr ?? searchParamsState;
  });
  const activeFilterParams = optimisticState[uniqueFilterName];
  let placeholderText = translate(placeholder);
  let isSomeOptionSelected = false;
  if (activeFilterParams && activeFilterParams.length > 0) {
    isSomeOptionSelected = true;
    placeholderText = `1 ${translate(selectedText ?? '')}`;
    if (activeFilterParams.length > 1 && selectedTextPlural) {
      placeholderText = `${activeFilterParams.length} ${translate(selectedTextPlural)}`;
    }
  }
  const dropdownValues = useMemo(
    () => (isEmpty(activeFilterParams) ? undefined : { [id]: activeFilterParams }),
    [id, activeFilterParams],
  );

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const nativeSelect = wrapperRef.current.querySelector('select');
    if (nativeSelect) {
      Array.from(nativeSelect.options).forEach((option) => {
        const value = option.value;
        if (dropdownValues?.[id]?.includes(value)) {
          option.selected = true;
        } else {
          option.selected = false;
        }
      });
    }
  }, [id, uniqueFilterName, fsSectionId, translate, label, dropdownValues]);

  const handleChange = debounce((value) => {
    setStartTransition(() => {
      setOptimisticState({ [uniqueFilterName]: value.map((item) => item.value) });
      setMultipleSelectedParams({
        [uniqueFilterName]: value.map((item) => item.value),
      });
    });
  }, 300);

  if (isEmpty(allItems)) {
    return null;
  }

  return (
    <div ref={wrapperRef}>
      <Dropdown
        multiple
        placeholder={placeholderText}
        className={styles['tiles-filter-dropdown']}
        label={translate(label)}
        items={allItems}
        onChange={handleChange}
        name={id}
        values={dropdownValues}
        outlined
        forcePlaceholder
        forceCustom
        placeholderClassName={isSomeOptionSelected ? styles['tiles-filter-placeholder'] : undefined}
      />
    </div>
  );
}
